import React, { useState } from 'react';
import { Box, Typography, Button, IconButton, useMediaQuery, useTheme } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import StoreIcon from '@mui/icons-material/Store';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import EditIcon from '@mui/icons-material/Edit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CloseIcon from '@mui/icons-material/Close';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import { alpha } from '@mui/material/styles';
import { useCurrency } from './CurrencyContext';
import { useCart } from './CartContext';
import CurrencySelector from './CurrencySelector';

const AccountDrawer = ({
  user,
  setUser,
  navigate,
  t,
  i18n,
  theme: themeFromProps,
  cities = [],
  selectedBranch,
  setBranchSelectorOpen,
  toggleLanguage,
  onClose
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallScreen = useMediaQuery('(max-width:400px)');
  
  const { selectedCurrency, getCurrencySymbol } = useCurrency();
  const { logout } = useCart();
  const [currencySelectorOpen, setCurrencySelectorOpen] = useState(false);

  const handleLogout = async () => {
    try {
      const userObj = JSON.parse(localStorage.getItem('user'));
      const userId = userObj?.Id || userObj?.id;
      if (userId) {
        localStorage.removeItem(`cart_${userId}`);
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
    await logout();
    onClose && onClose();
    window.location.reload();
  };

  return (
    <Box sx={{ 
      width: '100%',
      bgcolor: 'custom.white', 
      height: '100vh',
      overflowY: 'auto',
      paddingTop: { xs: '50px', sm: '60px', md: '88px' },
      paddingBottom: 2,
      position: 'relative'
    }}>
      {/* FLOATING CLOSE BUTTON - TOP RIGHT */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'fixed',
          top: { xs: 8, sm: 12 },
          right: { xs: 8, sm: 12 },
          zIndex: 1400,
          bgcolor: 'primary.main',
          color: 'white',
          width: { xs: 36, sm: 40 },
          height: { xs: 36, sm: 40 },
          boxShadow: 3,
          '&:hover': {
            bgcolor: 'primary.dark',
            transform: 'scale(1.1)'
          },
          transition: 'all 0.2s'
        }}
      >
        <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
      </IconButton>

      {/* Header Section */}
      <Box sx={{ px: { xs: 1.5, sm: 2 }, mb: 1 }}>
        {user ? (
          <Box sx={{
            display: 'flex', 
            alignItems: 'center', 
            bgcolor: 'custom.white', 
            p: { xs: 1, sm: 1.5 },
            borderRadius: { xs: '12px', sm: '16px' },
            boxShadow: `0 2px 8px ${alpha((themeFromProps || theme).palette.common.black, 0.05)}`,
            border: '1px solid', 
            borderColor: 'divider', 
            position: 'relative'
          }}>
            <Box sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: '50%', 
              bgcolor: 'grey.200',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              ml: { xs: 0.5, sm: 1 },
              border: '2px solid', 
              borderColor: 'custom.white', 
              boxShadow: `0 2px 4px ${alpha((themeFromProps || theme).palette.common.black, 0.1)}`,
              flexShrink: 0
            }}>
              <Typography sx={{ 
                fontSize: { xs: 16, sm: 20 },
                fontWeight: 'bold', 
                color: 'primary.main' 
              }}>
                {user.FullName ? user.FullName.charAt(0).toUpperCase() : 'U'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, mx: { xs: 1, sm: 1.5 } }}>
              <Typography sx={{ 
                fontSize: { xs: 13, sm: 15 },
                fontWeight: 'bold', 
                color: 'primary.main',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {user.FullName || t('account.defaultUser')}
              </Typography>
              <Typography sx={{ 
                fontSize: { xs: 10, sm: 11 }, 
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {user.Email || user.Phone || t('account.noContactInfo')}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => { onClose && onClose(); navigate('/profile'); }}
              sx={{ 
                position: 'absolute', 
                top: { xs: 4, sm: 6 }, 
                left: { xs: 4, sm: 6 }, 
                bgcolor: 'grey.100',
                width: { xs: 24, sm: 28 },
                height: { xs: 24, sm: 28 }
              }}
            >
              <EditIcon sx={{ fontSize: { xs: 12, sm: 14 }, color: 'primary.main' }} />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{
            bgcolor: 'custom.white', 
            p: { xs: 1.5, sm: 2 }, 
            borderRadius: { xs: '12px', sm: '16px' }, 
            textAlign: 'center',
            boxShadow: `0 2px 8px ${alpha((themeFromProps || theme).palette.common.black, 0.05)}`,
            border: '1px solid', 
            borderColor: 'divider'
          }}>
            <Typography sx={{ 
              fontSize: { xs: 16, sm: 18 }, 
              fontWeight: 'bold', 
              color: 'primary.main', 
              mb: 0.5 
            }}>
              {t('account.welcome')}
            </Typography>
            <Typography sx={{ 
              fontSize: { xs: 11, sm: 12 }, 
              color: 'text.secondary', 
              mb: { xs: 1.5, sm: 2 } 
            }}>
              {t('account.loginToTrack')}
            </Typography>
            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => { onClose && onClose(); navigate('/login'); }}
                sx={{ 
                  bgcolor: 'primary.main', 
                  borderRadius: { xs: '10px', sm: '12px' }, 
                  py: { xs: 1, sm: 1.2 }, 
                  fontWeight: 'bold', 
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&:active': { transform: 'scale(0.98)' }
                }}
              >
                {t('account.login')}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => { onClose && onClose(); navigate('/register'); }}
                sx={{ 
                  borderColor: 'divider', 
                  color: 'primary.main', 
                  borderRadius: { xs: '10px', sm: '12px' }, 
                  py: { xs: 1, sm: 1.2 }, 
                  fontWeight: 'bold',
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'transparent' },
                  '&:active': { transform: 'scale(0.98)' }
                }}
              >
                {t('account.signup')}
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Menu Sections */}
      <Box sx={{ px: { xs: 1.5, sm: 2 } }}>
        {/* Account Section */}
        <Typography sx={{ 
          fontSize: { xs: 11, sm: 12 }, 
          fontWeight: 'bold', 
          color: 'text.secondary', 
          mb: { xs: 1, sm: 1.5 }, 
          ml: 0.5 
        }}>
          {t('account.title')}
        </Typography>
        <Box sx={{ 
          bgcolor: 'custom.white', 
          borderRadius: { xs: '12px', sm: '16px' }, 
          overflow: 'hidden', 
          border: '1px solid', 
          borderColor: 'divider', 
          mb: { xs: 1.5, sm: 2 } 
        }}>
          {[
            { icon: <ReceiptLongOutlinedIcon />, title: t('account.myOrders'), subtitle: t('account.myOrdersSubtitle'), action: () => user ? navigate('/order-history') : navigate('/login') },
            { icon: <LocationOnOutlinedIcon />, title: t('account.trackOrder'), subtitle: t('account.trackOrderSubtitle'), action: () => user ? navigate('/track-orders') : navigate('/login') },
            { icon: <FavoriteBorderIcon />, title: t('account.favorites'), action: () => user ? navigate('/favorites') : navigate('/login') },
            { icon: <MapOutlinedIcon />, title: t('account.addresses'), subtitle: t('account.addressesSubtitle'), action: () => user ? navigate('/locations') : navigate('/login') },
            { icon: <ReceiptLongOutlinedIcon />, title: t('account.myRentals', 'My Rentals'), subtitle: t('account.myRentalsSubtitle', 'Track your rental orders'), action: () => user ? navigate('/my-rentals') : navigate('/login') },
          ].map((item, index, arr) => (
            <Box
              key={index}
              onClick={() => { onClose && onClose(); item.action(); }}
              sx={{
                display: 'flex', 
                alignItems: 'center', 
                p: { xs: 1, sm: 1.5 }, 
                cursor: 'pointer',
                borderBottom: index < arr.length - 1 ? 1 : 0, 
                borderColor: 'grey.100',
                '&:hover': { bgcolor: 'action.hover' },
                '&:active': { bgcolor: 'action.selected', transform: 'scale(0.99)' }
              }}
            >
              <Box sx={{
                width: { xs: 32, sm: 36 }, 
                height: { xs: 32, sm: 36 }, 
                borderRadius: { xs: '8px', sm: '10px' }, 
                bgcolor: 'grey.50',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                ml: { xs: 0.5, sm: 1 },
                flexShrink: 0
              }}>
                {React.cloneElement(item.icon, { sx: { color: 'primary.main', fontSize: { xs: 18, sm: 20 } } })}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, mx: { xs: 1, sm: 1.5 } }}>
                <Typography sx={{ 
                  fontSize: { xs: 12, sm: 14 }, 
                  fontWeight: '600', 
                  color: 'primary.main',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {item.title}
                </Typography>
                {item.subtitle && (
                  <Typography sx={{ 
                    fontSize: { xs: 9, sm: 10 }, 
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {item.subtitle}
                  </Typography>
                )}
              </Box>
              <ChevronLeftIcon sx={{ color: 'grey.400', fontSize: { xs: 16, sm: 18 }, flexShrink: 0 }} />
            </Box>
          ))}
        </Box>

        {/* Settings Section */}
        <Typography sx={{ 
          fontSize: { xs: 11, sm: 12 }, 
          fontWeight: 'bold', 
          color: 'text.secondary', 
          mb: { xs: 1, sm: 1.5 }, 
          ml: 0.5 
        }}>
          {t('account.settings')}
        </Typography>
        <Box sx={{ 
          bgcolor: 'custom.white', 
          borderRadius: { xs: '12px', sm: '16px' }, 
          overflow: 'hidden', 
          border: '1px solid', 
          borderColor: 'divider', 
          mb: { xs: 1.5, sm: 2 } 
        }}>
          {/* Notifications */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            p: { xs: 1, sm: 1.5 }, 
            borderBottom: 1, 
            borderColor: 'grey.100' 
          }}>
            <Box sx={{
              width: { xs: 32, sm: 36 }, 
              height: { xs: 32, sm: 36 }, 
              borderRadius: { xs: '8px', sm: '10px' }, 
              bgcolor: 'grey.50',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              ml: { xs: 0.5, sm: 1 }
            }}>
              <NotificationsOutlinedIcon sx={{ color: 'primary.main', fontSize: { xs: 18, sm: 20 } }} />
            </Box>
            <Box sx={{ flex: 1, mx: { xs: 1, sm: 1.5 } }}>
              <Typography sx={{ fontSize: { xs: 12, sm: 14 }, fontWeight: '600', color: 'primary.main' }}>
                {t('account.notifications')}
              </Typography>
            </Box>
            <Box sx={{ 
              width: { xs: 32, sm: 36 }, 
              height: { xs: 18, sm: 20 }, 
              bgcolor: 'primary.main', 
              borderRadius: 12, 
              position: 'relative' 
            }}>
              <Box sx={{ 
                width: { xs: 14, sm: 16 }, 
                height: { xs: 14, sm: 16 }, 
                bgcolor: 'custom.white', 
                borderRadius: '50%', 
                position: 'absolute', 
                top: 2, 
                left: 2 
              }} />
            </Box>
          </Box>

          {/* Currency */}
          <Box 
            onClick={() => setCurrencySelectorOpen(true)} 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              p: { xs: 1, sm: 1.5 }, 
              borderBottom: 1, 
              borderColor: 'grey.100', 
              cursor: 'pointer', 
              '&:hover': { bgcolor: 'action.hover' },
              '&:active': { bgcolor: 'action.selected', transform: 'scale(0.99)' }
            }}
          >
            <Box sx={{
              width: { xs: 32, sm: 36 }, 
              height: { xs: 32, sm: 36 }, 
              borderRadius: { xs: '8px', sm: '10px' }, 
              bgcolor: 'grey.50',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              ml: { xs: 0.5, sm: 1 }
            }}>
              <AttachMoneyIcon sx={{ color: 'primary.main', fontSize: { xs: 18, sm: 20 } }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, mx: { xs: 1, sm: 1.5 } }}>
              <Typography sx={{ 
                fontSize: { xs: 12, sm: 14 }, 
                fontWeight: '600', 
                color: 'primary.main',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {t('currency.title') || 'Currency'}
              </Typography>
              <Typography sx={{ fontSize: { xs: 9, sm: 10 }, color: 'text.secondary' }}>
                {getCurrencySymbol()} {selectedCurrency}
              </Typography>
            </Box>
            <ChevronLeftIcon sx={{ color: 'grey.400', fontSize: { xs: 16, sm: 18 } }} />
          </Box>

          {/* Language */}
          <Box 
            onClick={toggleLanguage} 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              p: { xs: 1, sm: 1.5 }, 
              borderBottom: 1, 
              borderColor: 'grey.100', 
              cursor: 'pointer', 
              '&:hover': { bgcolor: 'action.hover' },
              '&:active': { bgcolor: 'action.selected', transform: 'scale(0.99)' }
            }}
          >
            <Box sx={{
              width: { xs: 32, sm: 36 }, 
              height: { xs: 32, sm: 36 }, 
              borderRadius: { xs: '8px', sm: '10px' }, 
              bgcolor: 'grey.50',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              ml: { xs: 0.5, sm: 1 }
            }}>
              <LanguageOutlinedIcon sx={{ color: 'primary.main', fontSize: { xs: 18, sm: 20 } }} />
            </Box>
            <Box sx={{ flex: 1, mx: { xs: 1, sm: 1.5 } }}>
              <Typography sx={{ fontSize: { xs: 12, sm: 14 }, fontWeight: '600', color: 'primary.main' }}>
                {t('account.language')}
              </Typography>
              <Typography sx={{ fontSize: { xs: 9, sm: 10 }, color: 'text.secondary' }}>
                {i18n.language === 'ar' ? 'العربية' : 'English'}
              </Typography>
            </Box>
            <ChevronLeftIcon sx={{ color: 'grey.400', fontSize: { xs: 16, sm: 18 } }} />
          </Box>

          {/* Go to Store */}
          <Box 
            onClick={() => navigate('/')} 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              p: { xs: 1, sm: 1.5 }, 
              borderBottom: 1, 
              borderColor: 'grey.100', 
              cursor: 'pointer', 
              '&:hover': { bgcolor: 'action.hover' },
              '&:active': { bgcolor: 'action.selected', transform: 'scale(0.99)' }
            }}
          >
            <Box sx={{
              width: { xs: 32, sm: 36 }, 
              height: { xs: 32, sm: 36 }, 
              borderRadius: { xs: '8px', sm: '10px' }, 
              bgcolor: 'grey.50',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              ml: { xs: 0.5, sm: 1 }
            }}>
              <StoreIcon sx={{ color: 'primary.main', fontSize: { xs: 18, sm: 20 } }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, mx: { xs: 1, sm: 1.5 } }}>
              <Typography sx={{ 
                fontSize: { xs: 12, sm: 14 }, 
                fontWeight: '600', 
                color: 'primary.main',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {t('branchSelector.title', 'Branch')}
              </Typography>
              <Typography sx={{ 
                fontSize: { xs: 9, sm: 10 }, 
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {selectedBranch ? selectedBranch.name : t('branchSelector.select', 'Select Branch')}
              </Typography>
            </Box>
            <ChevronLeftIcon sx={{ color: 'grey.400', fontSize: { xs: 16, sm: 18 } }} />
          </Box>

          {/* Select Branch */}
          <Box 
            onClick={() => navigate('/select-branch')} 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              p: { xs: 1, sm: 1.5 }, 
              borderBottom: 1, 
              borderColor: 'grey.100', 
              cursor: 'pointer', 
              '&:hover': { bgcolor: 'action.hover' },
              '&:active': { bgcolor: 'action.selected', transform: 'scale(0.99)' }
            }}
          >
            <Box sx={{
              width: { xs: 32, sm: 36 }, 
              height: { xs: 32, sm: 36 }, 
              borderRadius: { xs: '8px', sm: '10px' }, 
              bgcolor: 'grey.50',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              ml: { xs: 0.5, sm: 1 }
            }}>
              <StoreIcon sx={{ color: 'primary.main', fontSize: { xs: 18, sm: 20 } }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, mx: { xs: 1, sm: 1.5 } }}>
              <Typography sx={{ 
                fontSize: { xs: 12, sm: 14 }, 
                fontWeight: '600', 
                color: 'primary.main',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {t('branchSelector.title2', 'Branch')}
              </Typography>
              <Typography sx={{ 
                fontSize: { xs: 9, sm: 10 }, 
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {selectedBranch ? selectedBranch.name : t('branchSelector.select', 'Select Branch')}
              </Typography>
            </Box>
            <ChevronLeftIcon sx={{ color: 'grey.400', fontSize: { xs: 16, sm: 18 } }} />
          </Box>

          {/* City */}
          <Box sx={{ p: { xs: 1, sm: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 0.5, sm: 1 } }}>
              <Box sx={{
                width: { xs: 32, sm: 36 }, 
                height: { xs: 32, sm: 36 }, 
                borderRadius: { xs: '8px', sm: '10px' }, 
                bgcolor: 'grey.50',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                ml: { xs: 0.5, sm: 1 }
              }}>
                <BusinessOutlinedIcon sx={{ color: 'primary.main', fontSize: { xs: 18, sm: 20 } }} />
              </Box>
              <Box sx={{ mx: { xs: 1, sm: 1.5 } }}>
                <Typography sx={{ fontSize: { xs: 12, sm: 14 }, fontWeight: '600', color: 'primary.main' }}>
                  {t('account.city')}
                </Typography>
                <Typography sx={{ fontSize: { xs: 9, sm: 10 }, color: 'text.secondary' }}>
                  {user?.City || user?.city || t('account.selectCity')}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {cities.slice(0, 3).map(city => (
                <Box
                  key={city.Id || city.id}
                  onClick={async () => {
                    if (!user) return;
                    const updatedUser = { ...user, City: city.Name || city.name };
                    setUser(updatedUser);
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                  }}
                  sx={{
                    px: { xs: 1, sm: 1.2 }, 
                    py: { xs: 0.3, sm: 0.4 }, 
                    borderRadius: { xs: '8px', sm: '10px' },
                    bgcolor: (user?.City === (city.Name || city.name)) ? 'primary.main' : 'grey.200',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:active': { transform: 'scale(0.95)' }
                  }}
                >
                  <Typography sx={{
                    fontSize: { xs: 10, sm: 11 }, 
                    fontWeight: '600',
                    color: (user?.City === (city.Name || city.name)) ? 'custom.white' : 'text.secondary'
                  }}>
                    {city.Name || city.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Support Section */}
        <Typography sx={{ 
          fontSize: { xs: 11, sm: 12 }, 
          fontWeight: 'bold', 
          color: 'text.secondary', 
          mb: { xs: 1, sm: 1.5 }, 
          ml: 0.5 
        }}>
          {t('account.support')}
        </Typography>
        <Box sx={{ 
          bgcolor: 'custom.white', 
          borderRadius: { xs: '12px', sm: '16px' }, 
          overflow: 'hidden', 
          border: '1px solid', 
          borderColor: 'divider', 
          mb: { xs: 1.5, sm: 2 } 
        }}>
          {[
            { icon: <HelpOutlineIcon />, title: t('account.helpCenter'), action: () => navigate('/support') },
            { icon: <InfoOutlinedIcon />, title: t('account.aboutUs'), action: () => navigate('/about-us') },
            { icon: <DescriptionIcon />, title: t('account.policiesTerms', 'Policies & Terms'), action: () => navigate('/policies-terms') },
          ].map((item, index, arr) => (
            <Box
              key={index}
              onClick={() => { onClose && onClose(); item.action && item.action(); }}
              sx={{
                display: 'flex', 
                alignItems: 'center', 
                p: { xs: 1, sm: 1.5 }, 
                cursor: 'pointer',
                borderBottom: index < arr.length - 1 ? 1 : 0, 
                borderColor: 'grey.100',
                '&:hover': { bgcolor: 'action.hover' },
                '&:active': { bgcolor: 'action.selected', transform: 'scale(0.99)' }
              }}
            >
              <Box sx={{
                width: { xs: 32, sm: 36 }, 
                height: { xs: 32, sm: 36 }, 
                borderRadius: { xs: '8px', sm: '10px' }, 
                bgcolor: 'grey.50',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                ml: { xs: 0.5, sm: 1 }
              }}>
                {React.cloneElement(item.icon, { sx: { color: 'primary.main', fontSize: { xs: 18, sm: 20 } } })}
              </Box>
              <Box sx={{ flex: 1, mx: { xs: 1, sm: 1.5 } }}>
                <Typography sx={{ fontSize: { xs: 12, sm: 14 }, fontWeight: '600', color: 'primary.main' }}>
                  {item.title}
                </Typography>
              </Box>
              <ChevronLeftIcon sx={{ color: 'grey.400', fontSize: { xs: 16, sm: 18 } }} />
            </Box>
          ))}
        </Box>

        {/* Logout */}
        {user && (
          <Button
            fullWidth
            onClick={handleLogout}
            sx={{
              bgcolor: 'custom.white', 
              color: 'error.main', 
              borderRadius: { xs: '12px', sm: '16px' }, 
              py: { xs: 1.2, sm: 1.5 },
              border: '1px solid', 
              borderColor: 'divider', 
              fontWeight: 'bold', 
              justifyContent: 'center',
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              mb: 1.5,
              '&:hover': { bgcolor: 'error.light', borderColor: 'error.light' },
              '&:active': { transform: 'scale(0.98)' }
            }}
          >
            <LogoutIcon sx={{ mr: 1, fontSize: { xs: 16, sm: 18 } }} />
            {t('account.logout')}
          </Button>
        )}

        <Typography sx={{ 
          textAlign: 'center', 
          color: 'grey.400', 
          fontSize: { xs: 10, sm: 11 }, 
          mb: 1
        }}>
          {t('account.version')} 1.0.0
        </Typography>
      </Box>

      <CurrencySelector open={currencySelectorOpen} onClose={() => setCurrencySelectorOpen(false)} />
    </Box>
  );
};

export default AccountDrawer;